// Server-side (and test-side) replay of a Daily Challenge run. Because the whole
// season is a pure function of (seed, the player's draft choices), the Worker can
// reconstruct a player's run from the date seed plus a tiny decision log, recompute
// the authoritative standings, and reject anything that doesn't add up — so the
// leaderboard can't be fed a fabricated score. This module is DOM-free (it only
// touches the engine + data layers), so it bundles unchanged into the Worker.
import type { Decision, Standings } from "../types";
import { Rng } from "./rng";
import {
  drawCalendar,
  drawRivals,
  newSeasonState,
  pickCard,
  pickDirector,
  rerollDirectorPack,
  rerollKeeping,
  rerollPack,
  rollPack,
} from "./draft";
import { drawWeather, runNextRace } from "./race";

// A pathological log can't be allowed to spin the engine forever. A real draft is
// ~6–10 actions; this cap is generous slack for rerolls while bounding the work.
export const MAX_DECISIONS = 200;

// The team-name cap, enforced identically on the client (the naming/rename
// inputs) and the server (the Worker sanitises the submitted name to this). Lives
// here because this is the one module both sides already share.
export const MAX_TEAM_NAME_LEN = 28;

export type ReplayResult =
  | { ok: true; standings: Standings }
  | { ok: false; reason: string };

/**
 * Re-simulate a Daily run from its seed and the player's decision log, returning
 * the final standings. Mirrors App.beginDraft's setup order EXACTLY — any
 * divergence desyncs the shared RNG stream and the score won't match the client.
 */
export function replayRun(seed: number, chaos: boolean, log: readonly Decision[]): ReplayResult {
  if (!Number.isFinite(seed)) return { ok: false, reason: "bad_seed" };
  if (!Array.isArray(log) || log.length > MAX_DECISIONS) return { ok: false, reason: "bad_log" };

  const s = newSeasonState("replay");
  s.seed = seed >>> 0;
  s.chaos = chaos;
  // fromMemory is a draft-time display mask only — it changes no draws and no
  // scores — so it is irrelevant to a replay.

  const rng = new Rng(s.seed);
  // Setup order must match beginDraft: calendar → rivals → weather → first pack.
  drawCalendar(rng, s);
  drawRivals(rng, s);
  drawWeather(rng, s);
  rollPack(rng, s);
  s.rollNo = 1;

  for (const d of log) {
    if (!d || typeof d !== "object") return { ok: false, reason: "bad_decision" };
    switch (d.t) {
      case "pick":
        pickCard(rng, s, d.i | 0);
        break;
      case "reroll":
        rerollPack(rng, s);
        break;
      case "rerollKeep":
        rerollKeeping(rng, s, d.i | 0, d.keep === "country" ? "country" : "team");
        break;
      case "pickDir":
        pickDirector(rng, s, d.i | 0);
        break;
      case "rerollDir":
        rerollDirectorPack(rng, s);
        break;
      default:
        return { ok: false, reason: "bad_decision" };
    }
  }

  // The draft finalizes only when a director is chosen — only then is the player
  // prepended to s.opponents. A log that never gets there is an incomplete run.
  if (!s.slots.director || !s.opponents.some((o) => o.isPlayer)) {
    return { ok: false, reason: "incomplete" };
  }

  // Run every scheduled race; runNextRace computes standings on the final round.
  let guard = 0;
  while (s.round < s.calendar.length && guard++ <= s.calendar.length + 2) runNextRace(s);
  if (!s.standings) return { ok: false, reason: "no_standings" };
  return { ok: true, standings: s.standings };
}

// The leaderboard ranking fields for the player in a finished run. These are the
// same numbers buildDailyShareText surfaces, so client and server always agree.
export interface PlayerScore {
  readonly teamPts: number;
  readonly driverPts: number;
  readonly wccPos: number;
}

/** Extract the player's score fields from finished standings, or null if absent. */
export function playerScore(st: Standings): PlayerScore | null {
  const wccPos = st.constructors.findIndex((c) => c.isPlayer) + 1;
  if (wccPos === 0) return null;
  const teamPts = st.constructors.find((c) => c.isPlayer)?.points ?? 0;
  const driverPts = st.drivers.filter((d) => d.isPlayer).reduce((sum, d) => sum + d.points, 0);
  return { teamPts, driverPts, wccPos };
}

// Collapse the ranking into one sortable integer: Constructors' points first,
// drivers' points as the tiebreak. driverPts is comfortably below the multiplier,
// so team points always dominate. Shared by the Worker's INSERT and ORDER BY.
export const scoreScalar = (teamPts: number, driverPts: number): number =>
  teamPts * 100000 + driverPts;
